import React, { useEffect, useReducer, useState } from 'react';
import { FunctionalLoader, IntentBehaviorProvider, Typography, useDidUpdate, useGlobalContext, usePrevious, useStateInRef } from 'seagull';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { useStore, useStoreActions } from "../../contexts";
import StripeElementsProvider from "../../providers/StripeElementsProvider";
import { handleRequest } from "../../infra/utils";
import { RequestAccountantAccess, requestAccountantAccessModel } from "../../models/request-accountant-access";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useOnMount } from "../../hooks/useOnMount";
import { selectors } from "../../store/state-selectors";
import { ErrorText, routes } from "../../constants";
import ApproveTermsStep from "./ApproveTermsStep/ApproveTermsStep";
import WelcomeStep from "./WelcomeStep/WelcomeStep";
import ConnectPaymentMethodStep from "./ConnectPaymentMethodStep/ConnectPaymentMethodStep";
import { useEventReporter } from "../../event-reporter";
import { CobStorageProvider, useCobStorage } from "./CobStorageContext";
import { changeBusinessLoader } from "./WelcomeStep/welcomeStepUtils";
import PackagesStep from "./PackagesStep/PackagesStep";
import { ResponsiveProvider } from "../contexts";
import styles from "./ClientOnboarding.module.scss";
import ReviewServicesStep from "./ReviewServicesStep/ReviewServicesStep";
import AccountantAccessStep from "./AccountantAccessStep/AccountantAccessStep";
import RestfulWrapper from "../../components/RestfulWrapper/RestfulWrapper";
import { relationshipModel, serviceModel } from "../../models";
import { clientOnboardingNavigateStateReducer, COBStep, getInitialSelectedBundleId, getInitialSelectedOptionalServices, getInitialSelectedPaymentMethod, getReachableSteps, getStepName, isAllowedToGoBack, PreviewState, Reason } from "./ClientOnboarding.utils";
import { ClientFlowStructure } from "../components/ClientFlowStructure";
import { CobConfigProvider } from "./CobConfigContext";
import { CobBrandingProvider } from "./CobBrandingContext";
import IntroVideoStep from "./IntroVideoStep/IntroVideoStep";
import { ServerError } from "../../infra/ErrorResponse";
import { getIntentBehavior } from "../../infra/access-control";
import PendingAdditionalSignatures from "./RelationshipSignedStep/PendingAdditionalSignatures";
export function ClientOnboarding(props) {
  const {
    relationship,
    currentStep,
    previewState,
    disableMobile,
    dismissPreviewModal,
    setCurrentStep,
    refresh
  } = props;
  const isPreview = !!previewState;
  const userId = useStore(selectors.user.id);
  const isReviewer = relationshipModel.isReviewer(relationship, userId);
  const featureFlags = useFeatureFlags();
  const isMemberClient = useStore(s => selectors.user.isMemberUser(s) && selectors.user.isClient(s));
  const {
    logBI,
    setRelationshipScope,
    api
  } = useStoreActions();
  const {
    reportToGtm,
    reportToAppcues,
    reportToHubspot,
    reportToHeap
  } = useEventReporter();
  const {
    getFromStorage,
    setToStorage,
    clearStorage
  } = useCobStorage(relationship);
  const reachableSteps = getReachableSteps(relationship);
  const globalContext = useGlobalContext();
  const [prevVisitedSteps, setPrevVisitedSteps] = useState(getFromStorage('prev_visited_steps') || []);
  const activeStepIndex = Math.max(reachableSteps.findIndex(s => s === currentStep), 0);
  const lastActiveStepIndex = usePrevious(activeStepIndex);
  const getStateInRef = useStateInRef({
    prevVisitedSteps,
    prevActiveStepIndex: lastActiveStepIndex,
    setToStorage
  });
  const [approveAgreementStatus, setApproveAgreementStatus] = useState(null);
  const [disabledReason, setDisabledReason] = useState();
  const [selectedPayment, setSelectedPayment] = useState(getInitialSelectedPaymentMethod(getFromStorage, relationship));
  const [selectedBundleID, setSelectedBundleID] = useState(getInitialSelectedBundleId(getFromStorage, relationship));
  const [selectedOptionalServices, _setSelectedOptionalServices] = useState(getInitialSelectedOptionalServices({
    selectedBundleID,
    getFromStorage,
    isPreview,
    relationship,
    setToStorage
  }));
  const previewStatus = isPreview ? dismissPreviewModal ? {
    inModal: true,
    onDismissPreviewModal: dismissPreviewModal
  } : {
    inModal: false
  } : undefined;
  const setSelectedOptionalServices = val => {
    _setSelectedOptionalServices(val);
    setToStorage('selected_optional_services', val);
  };
  const completedSteps = prevVisitedSteps.filter(stepIdx => {
    const step = reachableSteps[stepIdx];
    if (step === COBStep.selectAPackage) {
      return selectedBundleID != null;
    } else if (step === COBStep.paymentMethod) {
      return !!selectedPayment;
    } else if (step === COBStep.accountantAccess) {
      const checked = getFromStorage('request_accountant_access');
      return relationship.requestAccountantAccess !== RequestAccountantAccess.Mandatory || checked;
    } else {
      return true;
    }
  });
  useEffect(() => {
    const {
      prevActiveStepIndex,
      prevVisitedSteps,
      setToStorage
    } = getStateInRef();
    if (prevActiveStepIndex != null && !prevVisitedSteps.includes(prevActiveStepIndex)) {
      setPrevVisitedSteps(prevVisited => {
        const newPrevVisitedSteps = [...prevVisited, prevActiveStepIndex];
        setToStorage('prev_visited_steps', newPrevVisitedSteps);
        return newPrevVisitedSteps;
      });
    }
  }, [activeStepIndex]);
  useEffect(() => {
    if (currentStep && currentStep !== COBStep.welcome) {
      if (currentStep === COBStep.allSet) {
        clearStorage();
      } else {
        setToStorage('last_step', currentStep);
      }
    }
  }, [clearStorage, currentStep, setToStorage]);
  useOnMount(() => {
    setRelationshipScope({
      id: relationship.id,
      isApproved: relationshipModel.isApproved(relationship)
    });
    if (!isPreview) {
      if (relationship.isSample) {
        logBI(11903);
        reportToGtm('notification-open', 'vendor-opened-test-agreement');
        reportToAppcues('notification-open', 'vendor-opened-test-agreement');
        reportToHeap('testAgreement', 'load', 'vendor-opened-test-agreement');
      } else {
        logBI(10824, {
          relationshipId: relationship.id
        });
        reportToHeap('clientOb', 'load', 'agreement', featureFlags);
      }
      if (window.heap != null) {
        window.heap.addUserProperties(featureFlags);
      }
    }
    if (relationshipModel.hasUserSigned(relationship, userId)) {
      setCurrentStep(COBStep.allSet);
    }
    return () => setRelationshipScope(null);
  });
  function changeSelectedPayment(selectedPayment) {
    setSelectedPayment(selectedPayment);
    if (selectedPayment) {
      setToStorage('selected_payment_method', selectedPayment);
    }
  }
  function navigateFrom(currentStep) {
    const currentIdx = reachableSteps.indexOf(currentStep);
    return {
      goNext: () => setCurrentStep(reachableSteps[currentIdx + 1]),
      goBack: () => setCurrentStep(reachableSteps[currentIdx - 1])
    };
  }
  async function handleAgreementSubmission(fundingSourceId, selectedBundleId) {
    const loaderName = 'lastStep';
    globalContext.startLoader(loaderName);
    const {
      success,
      error
    } = await handleRequest(api.relationship.approve(relationship, {
      fundingSourceId,
      selectedBundleId,
      selectedOptionalServices: getFromStorage('selected_optional_services'),
      requestAccountantAccessApproved: requestAccountantAccessModel.wasRequested(relationship.requestAccountantAccess)
    }));
    if (!success) {
      if (error.hasServerError(ServerError.MismatchingRelationshipVersion)) {
        globalContext.showErrorToast(ErrorText.MismatchingRelationshipVersion);
      }
      if (error.hasServerError(ServerError.InvalidRelationshipStatus)) {
        globalContext.showErrorToast('The proposal was canceled');
      }
      globalContext.stopLoader(loaderName);
      throw error;
    }
    globalContext.stopLoader(loaderName);
  }
  useEffect(() => {
    if (!relationship.isSample && reachableSteps.includes(COBStep.paymentMethod) && !selectedPayment) {
      setDisabledReason(Reason.PaymentMethod);
      return;
    }
    if (reachableSteps.includes(COBStep.selectAPackage) && !selectedBundleID) {
      setDisabledReason(Reason.SelectAPackage);
      return;
    }
    if (!relationshipModel.actualServices(relationship, selectedBundleID).some(service => serviceModel.isMandatory(service.participation) || selectedOptionalServices.includes(service.id))) {
      setDisabledReason(Reason.ReviewServices);
      return;
    }
    setDisabledReason(undefined);
  }, [selectedPayment, relationship, selectedBundleID, selectedOptionalServices, reachableSteps]);
  async function approveAgreement() {
    if (relationship.isSample) {
      logBI(11904);
    } else {
      logBI(10558);
    }
    try {
      const navigateToPendingRelationshipPage = () => navigateFrom(currentStep).goNext();
      setApproveAgreementStatus('loading');
      await handleAgreementSubmission(selectedPayment === null || selectedPayment === void 0 ? void 0 : selectedPayment.fundingSourceId, selectedBundleID);
      if (relationship.isSample) {
        reportToGtm('button-click', 'vendor-approved-test-agreement');
        reportToAppcues('button-click', 'vendor-approved-test-agreement');
        reportToHeap('testAgreement', 'click', 'vendor-approved-test-agreement');
        reportToHubspot('vendor_approved_test_agreement');
      } else {
        const paymentType = selectedPayment && selectedPayment.type === 'bankAccount' && (selectedPayment.status === 'new' || selectedPayment.status === 'validated') ? 'microDeposit' : selectedPayment === null || selectedPayment === void 0 ? void 0 : selectedPayment.type;
        reportToGtm('button-click', 'client-approved-agreement', {
          paymentType
        });
        reportToAppcues('button-click', 'client-approved-agreement');
        reportToHubspot('client-approved-agreement', {
          paymentType
        });
        reportToHeap('clientOb', 'click', 'client-approved-agreement', {
          paymentType
        });
      }
      setApproveAgreementStatus(null);
      const {
        relationship: updatedRelationship
      } = await refresh();
      relationshipModel.isPending(updatedRelationship) && navigateToPendingRelationshipPage();
    } catch (e) {
      setApproveAgreementStatus('error');
      globalContext.showErrorToast(ErrorText.UnexpectedErrorTryAgain, 'approve-agreement-error-toast');
    }
  }
  function dynamicStepsRenderer(step) {
    switch (step) {
      case COBStep.welcome:
        {
          const {
            goNext
          } = navigateFrom(COBStep.welcome);
          return /*#__PURE__*/React.createElement(WelcomeStep, {
            onDone: goNext
          });
        }
      case COBStep.selectAPackage:
        {
          const {
            goNext,
            goBack
          } = navigateFrom(COBStep.selectAPackage);
          return /*#__PURE__*/React.createElement(PackagesStep, {
            selectedBundleID: selectedBundleID,
            onSelectBundleID: id => {
              setToStorage('selected_bundle_id', id);
              setSelectedBundleID(id);
              setSelectedOptionalServices(relationshipModel.includedByDefaultOptionalServices(relationship, id));
            },
            onClickNext: goNext,
            onClickBack: goBack
          });
        }
      case COBStep.reviewServices:
        {
          const {
            goNext,
            goBack
          } = navigateFrom(COBStep.reviewServices);
          return /*#__PURE__*/React.createElement(ReviewServicesStep, {
            selectedBundleID: selectedBundleID,
            selectedOptionalServices: selectedOptionalServices,
            setSelectedOptionalServices: setSelectedOptionalServices,
            onDone: goNext,
            onBack: goBack
          });
        }
      case COBStep.approveTerms:
        {
          const {
            goNext,
            goBack
          } = navigateFrom(COBStep.approveTerms);
          const isLastStep = reachableSteps[reachableSteps.length - 2] === step;
          return /*#__PURE__*/React.createElement(ApproveTermsStep, {
            selectedBundleID: selectedBundleID,
            selectedOptionalServices: selectedOptionalServices,
            previewStatus: previewStatus,
            onDone: goNext,
            onBack: goBack,
            disabledReason: isReviewer && isLastStep ? Reason.ReviewerApprove : !isPreview && isLastStep ? disabledReason : undefined,
            approveAgreement: approveAgreement,
            isLastStep: isLastStep,
            approveAgreementStatus: approveAgreementStatus
          });
        }
      case COBStep.accountantAccess:
        {
          const {
            goNext,
            goBack
          } = navigateFrom(COBStep.accountantAccess);
          return /*#__PURE__*/React.createElement(AccountantAccessStep, {
            onBack: goBack,
            onDone: goNext
          });
        }
      case COBStep.paymentMethod:
        {
          const {
            goBack
          } = navigateFrom(COBStep.paymentMethod);
          return /*#__PURE__*/React.createElement(ConnectPaymentMethodStep, {
            selectedPaymentId: selectedPayment === null || selectedPayment === void 0 ? void 0 : selectedPayment.fundingSourceId,
            isMemberClient: isMemberClient,
            previewStatus: previewStatus,
            onSelectPaymentMethod: changeSelectedPayment,
            onDone: approveAgreement,
            onBack: goBack,
            disabledReason: isReviewer ? Reason.ReviewerApprove : isPreview ? undefined : disabledReason,
            approveAgreementStatus: approveAgreementStatus
          });
        }
      case COBStep.allSet:
        return /*#__PURE__*/React.createElement(PendingAdditionalSignatures, null);
      case COBStep.introVideo:
        {
          const {
            goNext,
            goBack
          } = navigateFrom(COBStep.introVideo);
          return /*#__PURE__*/React.createElement(IntroVideoStep, {
            onDone: goNext,
            onBack: goBack
          });
        }
      default:
        setCurrentStep(COBStep.welcome);
        return /*#__PURE__*/React.createElement(React.Fragment, null);
    }
  }
  return /*#__PURE__*/React.createElement("div", {
    "data-testid": "client-onboarding",
    className: styles.root
  }, isPreview && /*#__PURE__*/React.createElement(Typography, {
    weight: "normal",
    skin: "invertedStandard",
    className: styles.previewBanner,
    "data-testid": "cob-preview-banner"
  }, "You are in preview mode. No activity will be saved."), /*#__PURE__*/React.createElement("div", {
    className: styles.main
  }, /*#__PURE__*/React.createElement(ResponsiveProvider, {
    disableMobile: disableMobile
  }, /*#__PURE__*/React.createElement(CobConfigProvider, {
    relationship: relationship,
    previewState: previewState
  }, /*#__PURE__*/React.createElement(StripeElementsProvider, null, /*#__PURE__*/React.createElement(ClientFlowStructure, {
    hideBack: currentStep === COBStep.allSet,
    hideStepper: currentStep === COBStep.allSet,
    activeStepIndex: activeStepIndex,
    stepperSteps: reachableSteps.slice(0, -1).map(step => getStepName(step, relationship)),
    completedSteps: completedSteps,
    onStepClick: i => reachableSteps[i] && setCurrentStep(reachableSteps[i]),
    "data-testid": "clientob-pane",
    activeSteps: [...new Set([...prevVisitedSteps, activeStepIndex])]
  }, dynamicStepsRenderer(currentStep)))))));
}
export function ClientOnboardingRouted(props) {
  const {
    relationship
  } = props;
  const history = useHistory();
  const ff = useStore(selectors.ff.featureFlags);
  const businessId = useStore(selectors.user.businessId);
  const ffRelationshipVendorId = useStore(state => state.ff.relationshipVendorId);
  const {
    ff: {
      setRelationshipVendorId
    }
  } = useStoreActions();
  const {
    getFromStorage
  } = useCobStorage(relationship);
  const role = useStore(selectors.user.role);
  const userId = useStore(selectors.user.id);
  const fetchingFF = !ff.data || !ffRelationshipVendorId;
  const relationshipStatus = relationshipModel.status(relationship);
  const relationshipVendorId = relationshipModel.vendorID(relationship);
  const [clientOnboardingNavigateState, dispatch] = useReducer(clientOnboardingNavigateStateReducer, {
    urlToNavigate: null,
    relationshipStatus: relationshipModel.status(relationship),
    role,
    relationshipId: relationshipModel.id(relationship),
    hasStartedWithPendingStatus: relationshipModel.isPending(relationship),
    isPrimary: relationshipModel.isBuyer(relationship, userId)
  });
  useEffect(() => {
    if (clientOnboardingNavigateState.urlToNavigate) {
      const navigate = isAllowedToGoBack(clientOnboardingNavigateState.urlToNavigate) ? history.push : history.replace;
      navigate(clientOnboardingNavigateState.urlToNavigate);
    }
  }, [clientOnboardingNavigateState.urlToNavigate]);
  useEffect(() => {
    dispatch({
      type: 'RelationshipStatusChanged',
      status: relationshipStatus
    });
  }, [relationshipStatus]);
  useEffect(() => {
    setRelationshipVendorId(relationshipVendorId);
    return () => {
      setRelationshipVendorId();
    };
  }, [relationshipVendorId]);
  if (fetchingFF) {
    return /*#__PURE__*/React.createElement(FunctionalLoader, {
      name: 'fetchFF',
      show: true
    });
  }
  return /*#__PURE__*/React.createElement(Switch, null, /*#__PURE__*/React.createElement(Route, {
    path: "".concat(routes.clientOnboarding(), "/:stepId"),
    render: _ref => {
      let {
        history,
        match
      } = _ref;
      return /*#__PURE__*/React.createElement(CobBrandingProvider, {
        initialBranding: relationship.vendor.branding
      }, /*#__PURE__*/React.createElement(ClientOnboarding, {
        previewState:
        // A vendor can enter here by copying the proposal-link and pasting it in a new tab while logged in.
        relationship && relationship.vendor.id === businessId && !relationship.isSample ? PreviewState.Preview : undefined,
        refresh: props.refresh,
        relationship: relationship,
        currentStep: match.params.stepId,
        setCurrentStep: to => history.push(to)
      }));
    }
  }), /*#__PURE__*/React.createElement(Route, {
    path: routes.clientOnboarding()
  }, _ref2 => {
    var _getFromStorage;
    let {
      match
    } = _ref2;
    return /*#__PURE__*/React.createElement(Redirect, {
      to: "".concat(match.url, "/").concat((_getFromStorage = getFromStorage('last_step')) !== null && _getFromStorage !== void 0 ? _getFromStorage : COBStep.welcome)
    });
  }));
}
export function ClientOnboardingQueryWrapper(props) {
  const {
    relationshipId
  } = props.match.params;
  const businessId = useStore(selectors.user.businessId);
  const userRole = useStore(selectors.user.role);
  const userId = useStore(selectors.user.id);
  const {
    api
  } = useStoreActions();
  const globalContext = useGlobalContext();
  useDidUpdate(() => {
    globalContext.stopLoader(changeBusinessLoader);
  }, [businessId]);
  async function fetchData() {
    const [relationship, membersResponse] = await Promise.all([api.relationship.get(relationshipId), api.member.getAll()]);
    const shouldSetAccountants = requestAccountantAccessModel.wasRequested(relationship.requestAccountantAccess);
    return {
      relationship,
      accountants: shouldSetAccountants ? membersResponse.accountants : []
    };
  }
  return /*#__PURE__*/React.createElement(RestfulWrapper, {
    fetch: fetchData,
    renderChild: _ref3 => {
      let {
        data,
        refresh
      } = _ref3;
      return /*#__PURE__*/React.createElement(IntentBehaviorProvider, {
        value: getIntentBehavior(userRole, {
          relationship: data.relationship,
          userId
        })
      }, /*#__PURE__*/React.createElement(ClientOnboardingRouted, {
        relationship: data.relationship,
        refresh: refresh
      }));
    }
  });
}
export default function ClientOnboardingContextWrapper(props) {
  return /*#__PURE__*/React.createElement(CobStorageProvider, null, /*#__PURE__*/React.createElement(ClientOnboardingQueryWrapper, props));
}